import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getIncidents } from '../../services/serverMonitoringService';
import { formatRelativeDate } from '../../utils/relativeTime';
import {
  INCIDENTS_LIMIT,
  SOURCES_INCIDENT,
  STATUTS_INCIDENT,
  SEVERITES_INCIDENT,
  filtrerIncidents,
  compterIncidents,
  natureIncident,
  classeStatut,
  valeurAffichee,
  libelleSource,
  libelleStatut,
  libelleGravite
} from '../../utils/serverMonitoring';

/**
 * Sous-onglet « Incidents serveur » (lot I5) — LECTURE SEULE (DI5-9).
 *
 * Ce que l'écran montre : la collection `serverMonitoring` du projet portail, écrite par le SERVEUR
 * (rejeux épuisés, DNS, certificat, publication, nettoyage). Aujourd'hui, cette trace n'est lisible
 * que par une commande serveur (`scripts/show-monitoring-incidents.js`) : cet écran la rend au
 * support depuis le panneau.
 *
 * Ce que l'écran ne fait JAMAIS : aucune écriture, aucun acquittement, aucune clôture (DI5-9 — un
 * geste qui change l'état du support fera l'objet d'un cadrage dédié). Aucune cloche (DI5-10).
 *
 * Doctrine d'affichage : on TRADUIT le vocabulaire fermé du serveur (source, état, gravité) et on
 * affiche BRUT ce qui en sort ; un champ absent est rendu « — », jamais « undefined » (banc B10).
 */

const pastille = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';

const dateComplete = (date) => (date ? date.toLocaleString('fr-FR') : '');

/** Une ligne du tableau : domaine, client, source, nature/gravité, état, compteurs, erreur brute. */
const LigneIncident = ({ incident }) => {
  const nature = natureIncident(incident);

  return (
    <tr className="border-b border-base-200 align-top">
      <td className="px-3 py-3">
        <span className="text-sm font-medium text-base-content">{valeurAffichee(incident.domainName)}</span>
        <p className="text-xs font-mono text-base-content/60 mt-0.5">{valeurAffichee(incident.errorCode)}</p>
        {incident.trackingId && (
          <p className="text-[11px] text-base-content/50 mt-0.5">suivi : {incident.trackingId}</p>
        )}
      </td>
      <td className="px-3 py-3 text-sm">
        {incident.userId ? (
          <Link to={`/clients/${incident.userId}`} className="link link-primary">fiche client</Link>
        ) : (
          <span className="text-base-content/50">client non rattaché</span>
        )}
      </td>
      <td className="px-3 py-3 text-sm text-base-content/80">{libelleSource(incident.source)}</td>
      <td className="px-3 py-3">
        <span className={`${pastille} ${nature.classe}`}>{nature.label}</span>
        <p className="text-[11px] text-base-content/60 mt-1">{libelleGravite(incident.severity)}</p>
      </td>
      <td className="px-3 py-3">
        <span className={`${pastille} ${classeStatut(incident.status)}`}>{libelleStatut(incident.status)}</span>
      </td>
      <td className="px-3 py-3 text-sm text-base-content/80 whitespace-nowrap">
        {incident.occurrences} observation(s)
        {incident.attempts !== null && <><br />{incident.attempts} tentative(s)</>}
      </td>
      <td className="px-3 py-3 text-sm text-base-content/80 whitespace-nowrap"
        title={dateComplete(incident.lastOccurredAt)}>
        {incident.lastOccurredAt ? formatRelativeDate(incident.lastOccurredAt) : '—'}
      </td>
      <td className="px-3 py-3 text-xs font-mono text-error/90 break-all max-w-md">
        {valeurAffichee(incident.errorMessage)}
      </td>
    </tr>
  );
};

const IncidentsTab = () => {
  const [incidents, setIncidents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [source, setSource] = useState('tous');
  const [status, setStatus] = useState('tous');
  const [severity, setSeverity] = useState('tous');

  const charger = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      setIncidents(await getIncidents());
    } catch (error) {
      console.warn('IncidentsTab: incidents indisponibles', error?.message);
      setLoadError('Impossible de charger les incidents serveur.');
      setIncidents([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const visibles = useMemo(
    () => filtrerIncidents(incidents, { source, status, severity }),
    [incidents, source, status, severity]
  );
  const compteurs = useMemo(() => compterIncidents(incidents), [incidents]);
  const filtreActif = source !== 'tous' || status !== 'tous' || severity !== 'tous';
  const tronquee = incidents.length >= INCIDENTS_LIMIT;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
        <span className="ml-3 text-base-content/70">Chargement des incidents serveur...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-error/10 text-error p-4 rounded-md">
        <h3 className="font-bold">Incidents indisponibles</h3>
        <p className="text-sm">{loadError}</p>
        <p className="text-xs mt-1">
          La collection « serverMonitoring » est réservée aux comptes administrateurs : cette lecture
          a été refusée ou interrompue. Rien n’a été modifié.
        </p>
        <button type="button" className="btn btn-sm btn-error mt-2" onClick={charger}>
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-base-content">Incidents serveur</h3>
          <p className="text-sm text-base-content/70 mt-1">
            {compteurs.total} document(s) · {compteurs.aTraiter} à traiter
            {compteurs.traces > 0 && ` · ${compteurs.traces} trace(s) informative(s)`}
            {filtreActif && ` — sur ${compteurs.total} au total`}
          </p>
        </div>
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info/20 text-info border border-info/30">
          lecture seule
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <select className="select select-bordered select-sm" value={source}
          onChange={(event) => setSource(event.target.value)} aria-label="Filtrer par source">
          {SOURCES_INCIDENT.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>

        <select className="select select-bordered select-sm" value={status}
          onChange={(event) => setStatus(event.target.value)} aria-label="Filtrer par état de suivi">
          {STATUTS_INCIDENT.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>

        <select className="select select-bordered select-sm" value={severity}
          onChange={(event) => setSeverity(event.target.value)} aria-label="Filtrer par gravité">
          {SEVERITES_INCIDENT.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>

        {filtreActif && (
          <button type="button" className="btn btn-sm btn-ghost"
            onClick={() => { setSource('tous'); setStatus('tous'); setSeverity('tous'); }}>
            Réinitialiser les filtres
          </button>
        )}
      </div>

      <p className="text-xs text-base-content/60 mt-3">
        Source : collection « serverMonitoring » du projet portail, écrite par le SERVEUR (rejeux
        épuisés, DNS, certificat, publication, nettoyage). Le plus récent d’abord. Un « Incident (à
        traiter) » exige une action du support ; une « Trace » est un refus métier QU’A VU le client
        et qui n’appelle aucune action. Les filtres et le tri sont faits en mémoire (aucun index).
      </p>

      {visibles.length === 0 ? (
        <p className="text-sm text-base-content/60 py-6 text-center">
          {filtreActif
            ? 'Aucun incident ne correspond à ces filtres.'
            : 'Aucun incident serveur enregistré.'}
        </p>
      ) : (
        <div className="overflow-x-auto mt-3">
          <table className="w-full">
            <thead className="bg-base-200/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Domaine / code</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Client</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Source</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Nature</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">État</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Compteurs</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Dernière fois</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Erreur remontée</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((incident) => <LigneIncident key={incident.id} incident={incident} />)}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-base-content/50 mt-4">
        {`Lecture limitée à ${INCIDENTS_LIMIT} documents.`}
        {tronquee && ' La borne est atteinte : d’autres incidents peuvent exister au-delà.'}
        {' Aucune action possible depuis cet écran : ni acquittement, ni clôture.'}
      </p>
    </div>
  );
};

export default IncidentsTab;


